/**
 * Lab Betfair Net Exposure `fetchBetfairBooks` —
 * `getBetfairBook` per event Match Odds marketId.
 */

import { useMemo } from 'react'
import { useQueries } from '@tanstack/react-query'
import { cmApi } from '@/api/cmApi'
import type { BetfairBookResponse } from '@/api/map/workstation'
import { authHeaders } from '@/auth/session'
import { BOOKS_POLL_MS } from '@/api/hooks/net-exposure/useBooks'

export function useBetfairListBooks(marketIds: readonly string[], enabled: boolean) {
  const uniqueKey = marketIds
    .map((id) => id.trim())
    .filter(Boolean)
    .sort()
    .join('|')
  const unique = useMemo(() => (uniqueKey ? uniqueKey.split('|') : []), [uniqueKey])

  const queries = useQueries({
    queries: unique.map((marketId) => ({
      queryKey: ['cm', 'betfair-list-book', marketId] as const,
      queryFn: () => cmApi.getBetfairBook(authHeaders(), { marketId }),
      enabled: enabled && Boolean(marketId),
      refetchInterval: enabled ? BOOKS_POLL_MS : false,
      retry: false as const,
      placeholderData: (previous: BetfairBookResponse | undefined) => previous,
    })),
  })

  const byMarketId = useMemo(() => {
    const map = new Map<string, BetfairBookResponse>()
    for (let i = 0; i < unique.length; i++) {
      const id = unique[i]!
      const data = queries[i]?.data
      if (data) map.set(id, data)
    }
    return map
  }, [queries, unique])

  const failedIds = useMemo(() => {
    const ids: string[] = []
    for (let i = 0; i < unique.length; i++) {
      if (queries[i]?.isError) ids.push(unique[i]!)
    }
    return ids
  }, [queries, unique])

  return {
    byMarketId,
    failedIds,
    /** True until every market's first getBetfairBook settles (no pending shells). */
    isPending: unique.length > 0 && queries.some((q) => q.isPending),
    isFetching: queries.some((q) => q.isFetching),
    isError: failedIds.length > 0,
  }
}
